import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Loader2, Mail, ArrowRight, Home } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

interface PaymentSuccessData {
  success: boolean;
  message: string;
  org_name?: string;
  plan?: string;
  billing_cycle?: string;
  email?: string;
  next_steps?: string;
}

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentData, setPaymentData] = useState<PaymentSuccessData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingCount, setPollingCount] = useState(0);

  // Fetch payment success information
  const fetchPaymentInfo = async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get(
        `/api/organizations/stripe/payment-success/?session_id=${sessionId}`
      );

      if (response.data) {
        setPaymentData(response.data);
        setError(null);
      }
    } catch (err: any) {
      // Log error for debugging
      setError(err.response?.data?.error || 'Failed to verify payment status');
    } finally {
      setIsLoading(false);
    }
  };

  // Poll for organization approval status
  const pollOrganizationStatus = async () => {
    if (!paymentData?.email) return;

    try {
      // Here you might want to add an endpoint to check organization approval status
      // For now, we'll just update the polling count
      setPollingCount(prev => prev + 1);

      // Stop polling after 30 attempts (5 minutes if polling every 10 seconds)
      if (pollingCount >= 30) {
        return;
      }

      // In a real implementation, you'd check if the organization has been approved
      // and redirect the user or update the UI accordingly
    } catch (err) {
      // Log error for debugging
    }
  };

  useEffect(() => {
    fetchPaymentInfo();
  }, [sessionId]);

  useEffect(() => {
    if (paymentData?.success && pollingCount < 30) {
      const interval = setInterval(() => {
        pollOrganizationStatus();
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [paymentData, pollingCount]);

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
            <CardContent className="flex flex-col items-center justify-center space-y-4 p-8">
              <Loader2 className="size-8 animate-spin text-primary-400" />
              <p className="text-slate-300">Verifying your payment...</p>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-red-700/50 bg-slate-800/90 backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-red-400">
                Payment Verification Failed
              </CardTitle>
              <CardDescription className="text-slate-300">{error}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button asChild variant="outline" className="w-full">
                <Link to="/marketing/pricing">Back to Pricing</Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <motion.div
        className="w-full max-w-2xl"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
          <CardHeader className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-green-500/10"
            >
              <CheckCircle className="size-8 text-green-400" />
            </motion.div>
            <CardTitle className="text-2xl font-bold text-white">Payment Successful!</CardTitle>
            <CardDescription className="text-slate-300">
              {paymentData?.message || 'Your payment has been processed successfully.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Payment Details */}
            {paymentData && (
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {paymentData.org_name && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-slate-400">Organization</p>
                      <p className="text-lg font-semibold text-white">{paymentData.org_name}</p>
                    </div>
                  )}

                  {paymentData.plan && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-slate-400">Plan</p>
                      <div className="flex items-center space-x-2">
                        <Badge variant="default" className="bg-primary-600">
                          {paymentData.plan}
                        </Badge>
                        {paymentData.billing_cycle && (
                          <span className="text-sm text-slate-400">
                            ({paymentData.billing_cycle})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {paymentData.email && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-slate-400">Contact Email</p>
                    <p className="text-white">{paymentData.email}</p>
                  </div>
                )}
              </div>
            )}

            <Separator className="bg-slate-600" />

            {/* Next Steps */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="mt-1 flex size-6 items-center justify-center rounded-full bg-primary-600 text-xs text-white">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-white">Admin Review</p>
                    <p className="text-sm text-slate-400">
                      Our team will review your organization request within 1-2 business days.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="mt-1 flex size-6 items-center justify-center rounded-full bg-primary-600 text-xs text-white">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-white">Email Invitation</p>
                    <p className="text-sm text-slate-400">
                      You'll receive an email invitation to access your new organization.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="mt-1 flex size-6 items-center justify-center rounded-full bg-primary-600 text-xs text-white">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-white">Start Using KernLogic</p>
                    <p className="text-sm text-slate-400">
                      Begin managing your product information with our enterprise PIM platform.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {sessionId && (
              <div className="rounded-lg bg-slate-700/50 p-4">
                <div className="flex items-center space-x-2">
                  <Mail className="size-4 text-slate-400" />
                  <p className="text-sm text-slate-400">
                    Reference ID: <span className="font-mono text-slate-300">{sessionId}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3 pt-4 sm:flex-row sm:space-x-3 sm:space-y-0">
              <Button
                asChild
                className="flex-1 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600"
              >
                <Link to="/">
                  <Home className="mr-2 size-4" />
                  Return to Home
                </Link>
              </Button>

              <Button
                variant="outline"
                asChild
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700/50"
              >
                <Link to="/marketing/pricing">
                  <ArrowRight className="mr-2 size-4" />
                  View All Plans
                </Link>
              </Button>
            </div>

            {/* Support Information */}
            <div className="text-center">
              <p className="text-xs text-slate-400">
                Need help? Contact our support team at{' '}
                <a
                  href="mailto:support@kernlogic.com"
                  className="text-primary-400 hover:text-primary-300"
                >
                  support@kernlogic.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </main>
  );
}

export default PaymentSuccessPage;
